import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../api/apiClient';
import { usePreloaderStore, useConfirmationModalStore } from '../../../../../../stores';
import { PersonIcon, CoinIcon, TrashIcon } from '../../../../model/icons';
import { formatDt, personName } from './challengeUtils';
import useResetChain from './useResetChain';

// Карточка звена «на проверке»: видео, исполнитель, поимённые жалобы, вердикт + оборвать цепочку.
export default function FlaggedStepCard({ item, onChanged }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const { open: openConfirm } = useConfirmationModalStore();

    const { taskTitle, step, reports = [] } = item;
    const executor = step?.participant;
    const reward = step?.rewardAmount ?? 0;

    const verify = async (verdict) => {
        show();
        try {
            await apiClient.post(`/api/neighbor/admin/challenges/steps/${step.id}/verify?verdict=${verdict}`);
            enqueueSnackbar(verdict === 'REJECTED' ? 'Обман подтверждён, награда изъята' : 'Отмечено как честное', { variant: 'success' });
            onChanged();
        } catch (e) {
            const msg = e.response?.status === 409
                ? 'Вердикт по этому звену уже вынесен'
                : (e.response?.data?.message || 'Ошибка при вынесении вердикта');
            enqueueSnackbar(msg, { variant: e.response?.status === 409 ? 'warning' : 'error' });
            if (e.response?.status === 409) onChanged();
        } finally { hide(); }
    };

    const confirmReject = () => openConfirm(
        <div className="confirm-msg">
            <b>Признать обманом?</b>
            <p>С игрока «{personName(executor)}» будет изъято <b>{reward}</b> баллов (баланс может уйти в минус). Действие необратимо.</p>
        </div>,
        () => verify('REJECTED'),
        { confirmText: 'Изъять награду' }
    );

    const resetChain = useResetChain(onChanged);

    // Модерация контента: удаление видео звена без вердикта — награда и цепочка не меняются.
    const deleteVideo = () => openConfirm(
        <div className="confirm-msg">
            <b>Удалить видео звена?</b>
            <p>Видео пропадёт из сторис-фида и карточек. Вердикты, награда и цепочка не меняются. Действие необратимо.</p>
        </div>,
        async () => {
            show();
            try {
                await apiClient.delete(`/api/neighbor/admin/challenges/steps/${step.id}/video`);
                enqueueSnackbar('Видео удалено', { variant: 'success' });
                onChanged();
            } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при удалении видео', { variant: 'error' }); }
            finally { hide(); }
        },
        { confirmText: 'Удалить видео' }
    );

    return (
        <div className="ch-card">
            <div className="ch-head">
                <div className="ch-title">{taskTitle || 'Челлендж'} <span className="ch-step">звено #{step?.id}</span></div>
                <span className="ch-reward">{reward} <CoinIcon /></span>
            </div>

            <div className="ch-meta">
                <span className="ch-mrow"><span className="k">Исполнитель:</span> {personName(executor)}</span>
                {step?.passedBy && <span className="ch-mrow"><span className="k">Передал:</span> {personName(step.passedBy)}</span>}
                <span className="ch-mrow"><span className="k">Выполнено:</span> {formatDt(step?.completedAt)}</span>
            </div>

            {step?.videoUrl ? (
                <div className="media-delwrap">
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video className="ch-video" src={step.videoUrl} controls preload="metadata" />
                    <button type="button" className="media-del" title="Удалить видео" onClick={deleteVideo}><TrashIcon /></button>
                </div>
            ) : <div className="bt-empty">Видео недоступно</div>}

            <div className="ch-reports">
                <div className="ch-reports-head">Жалоб: {step?.reportCount ?? reports.length}</div>
                {reports.length > 0 && (
                    <div className="ch-reports-list">
                        {reports.map(r => (
                            <div className="ch-report" key={r.id}>
                                <PersonIcon />{personName(r.reporter)}
                                <span className="t">{formatDt(r.createdAt)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="ch-acts">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => resetChain(step.chainId)}>Оборвать цепочку</button>
                <div className="ch-verdict">
                    <button type="button" className="btn btn-ok btn-sm" onClick={() => verify('CONFIRMED')}>Всё честно</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={confirmReject}>Обман — изъять награду</button>
                </div>
            </div>
        </div>
    );
}

FlaggedStepCard.propTypes = {
    item: PropTypes.shape({
        taskTitle: PropTypes.string,
        chainId: PropTypes.number,
        step: PropTypes.object,
        reports: PropTypes.array
    }).isRequired,
    onChanged: PropTypes.func.isRequired
};
